import { z } from 'zod';
import { zPaymentGateway } from './enums';
import { zTextNullable } from './primitives';

/**
 * This is the schema for the `profile` attribute of the Cognito user.
 * Can not be more than 2048 characters (AWS limit).
 *
 * Important! (cache warning):
 * This data is cached by Cognito and requires a Cognito `tokenRefresh` event to bust the cache.
 * This happens automatically via Amplify, but if the Cognito attribute is updated manually via AWS Console UI
 * then the user will still have the cached values, unless they logout or there token is refreshed.
 */

export const creditCardSchema = z.object({
  expiryMonth: z.number(),
  expiryYear: z.number(),
  isPrimary: z.boolean().default(false),
  label: z.string().nullable().default(null), // TODO: Sanitise
  last4: z.string(), // Last 4 digits of the card number.
  paymentGateway: zPaymentGateway,
  paymentGatewayPaymentMethodId: z.string(),
});

export const creditCardStrictSchema = creditCardSchema.safeExtend({
  expiryMonth: z.number().min(1).max(12),
  expiryYear: z.number().min(2022).max(2099),
});
export type CreditCard = z.output<typeof creditCardSchema>;

/**
 * Use `name` rather than ids, because the menu may be "synced" from another legacy
 * system, which generates new ids for all menu items, modifiers, etc.
 *
 * Named `myUsualOrder` rather than `lastOrder` because it enabled more flexibility to
 * change how it works in the future (Ie use favourite approach, rather than last order).
 */

export const myUsualOrderItemSchema = z.object({
  menuItemName: z.string(), // Required
  modifierNames: z.array(z.string()).default([]),
  notes: z.string().default(''),
  sizeModifierName: z.string().nullable().default(null), // Array filter can return undefined
});
export type MyUsualOrderItem = z.output<typeof myUsualOrderItemSchema>;

export const restaurantCustomerSchema = z.object({
  // _creationTime: z.number().optional(),
  // I considered keeping Cognito user pool as the source of truth for phone number and username,
  // but it becomes complex to access these values from backend, because external service (aws) needs
  // to be queries from Convex "action" which does not allow for transaction commits.
  // _id: z.string().optional(),
  creditCards: z.array(creditCardSchema).default([]),
  deliveryAddresses: z.array(z.string()).default([]),
  email: z.string().nullable().default(null),
  isEnabled: z.boolean(),
  loyaltyCardBalance: z.number().default(0).nullable().default(null),
  myUsualOrderItems: z.array(myUsualOrderItemSchema).nullable().default(null),
  name: z.string().nullable().default(null),
  phoneNumber: z
    .string()
    .describe("Read only. Derived from Cognito user 'phone_number' attribute."),
  restaurantId: z.string().optional(),
  sub: z.string().default('User identity `subject` or AWS Cognito `sub` user attribute'),
  username: z.string().describe("Read only. Derived from Cognito 'username'."),
  /** `lastOrderSubmittedAt` is useful for keeping track of customer that have not ordered recently */
  lastOrderSubmittedAt: z.number().nullable().default(null),

  /**
   * - `consumer-native` push notifications.
   * - The user can only have one device with push notifications enabled at a time.
   * - Their last used device will take precedence.
   */
  pushNotificationConfigId: z.string().nullable().default(null).optional(),

  //devicePlatform: z.union([z.literal('ios'), z.literal('android')]).optional(),
  //lastLoggedInAt: z.number().optional(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type RestaurantCustomer = z.output<typeof restaurantCustomerSchema>;

export const restaurantCustomerStrictSchema = restaurantCustomerSchema.safeExtend({
  creditCards: z.array(creditCardStrictSchema).max(3),
  email: z.email().nullable(),
  myUsualOrderItems: z.array(myUsualOrderItemSchema).max(3).nullable(),
  name: zTextNullable,
});

export const restaurantCustomerCvxSchema = restaurantCustomerSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  restaurantId: z.string(),
  updatedAt: z.number(),
});
export type RestaurantCustomerCvx = z.output<typeof restaurantCustomerCvxSchema>;
